/* projectRules — per-project promoted-rule + custom-rule storage.
 *
 * Spec 07 §"Storage contract". Every component reads/writes rule
 * state through this module — never through localStorage directly.
 *
 * Why this exists (CLAUDE.md §1, §5):
 *  - rules used to be a single global array, so switching projects
 *    in the header did not change what was watching. The "Watching
 *    your project" headline was a lie.
 *  - Library and Builder both end with the same verb — "promote" —
 *    and both land here.
 *
 * Storage keys:
 *   - `${env.VITE_LS_PROJECT_RULES_PREFIX}${projectId}.rules.v1`
 *       → string[] of promoted rule ids
 *   - `${env.VITE_LS_PROJECT_RULES_PREFIX}${projectId}.custom-rules.v1`
 *       → CustomRule[] (user-authored extensions of DesignerRule)
 *
 * Legacy `hgm.applied.rules.v1` is read once on first access and
 * migrated into the active project's bucket, then cleared.
 */

import { env } from "./env";
import type { DesignerRule } from "../data/designerRules";

const LEGACY_GLOBAL_KEY = "hgm.applied.rules.v1";

/* First-touch seed — five canonical rules cover style cohesion,
 * lead-time risk, budget cap, approval ageing, and a Vastu/cultural
 * rule. Designers can demote any of them; the seed never re-appears
 * (we mark the project initialised). Sourced from DESIGNER_RULES
 * (real ids, no fabrication). */
const SEED_RULE_IDS: ReadonlyArray<string> = [
  "R-lead-vs-milestone",
  "R-budget-kitchen-25",
  "R-room-cohesion",
  "R-bath-faces-pooja",
  "R-approval-age",
];

/** User-authored rule. Extends DesignerRule with provenance. The same
 *  shape is reused so DESIGNER_RULES and CustomRule[] can live in one
 *  list at read time and the UI does not need to special-case. */
export interface CustomRule extends DesignerRule {
  author: "user";
  createdAt: string;
  /** Optional SPARQL preview captured at promote-time, for the Why-engine. */
  sparql?: string;
}

function rulesKey(projectId: string): string {
  return `${env.VITE_LS_PROJECT_RULES_PREFIX}${projectId}.rules.v1`;
}
function customKey(projectId: string): string {
  return `${env.VITE_LS_PROJECT_RULES_PREFIX}${projectId}.custom-rules.v1`;
}
function initKey(projectId: string): string {
  return `${env.VITE_LS_PROJECT_RULES_PREFIX}${projectId}.initialized.v1`;
}

function readJson<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}
function writeJson(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage quota / disabled — caller continues, state stays in memory. */
  }
}

/** One-time migration of the global pre-Spec-07 array into the
 *  active project's bucket. Returns the migrated list (or empty). */
function migrateLegacy(projectId: string): string[] {
  const legacy = readJson<string[]>(LEGACY_GLOBAL_KEY);
  if (!Array.isArray(legacy) || legacy.length === 0) return [];
  writeJson(rulesKey(projectId), legacy);
  try { localStorage.removeItem(LEGACY_GLOBAL_KEY); } catch { /* ignore */ }
  return legacy;
}

/** First-touch seed: write the canonical 5 rules so a brand-new
 *  project doesn't open with an empty watchlist. The sentinel
 *  prevents the seed from coming back if the user later demotes
 *  everything — designer intent wins. */
function seedIfFresh(projectId: string): string[] {
  try {
    if (localStorage.getItem(initKey(projectId))) return [];
  } catch { /* ignore */ }
  writeJson(rulesKey(projectId), SEED_RULE_IDS);
  try { localStorage.setItem(initKey(projectId), "1"); } catch { /* ignore */ }
  return [...SEED_RULE_IDS];
}

/** List the rule ids currently watching this project. */
export function listPromoted(projectId: string): string[] {
  const direct = readJson<string[]>(rulesKey(projectId));
  if (Array.isArray(direct)) return direct;
  const migrated = migrateLegacy(projectId);
  if (migrated.length > 0) {
    try { localStorage.setItem(initKey(projectId), "1"); } catch { /* ignore */ }
    return migrated;
  }
  return seedIfFresh(projectId);
}

/** Promote a rule (by id) to a project. Idempotent. */
export function promote(projectId: string, ruleId: string): string[] {
  const cur = listPromoted(projectId);
  if (cur.includes(ruleId)) return cur;
  const next = [...cur, ruleId];
  writeJson(rulesKey(projectId), next);
  return next;
}

/** Promote many rules at once. Returns the new list. */
export function promoteMany(projectId: string, ruleIds: readonly string[]): string[] {
  const cur = listPromoted(projectId);
  const set = new Set(cur);
  for (const id of ruleIds) set.add(id);
  const next = Array.from(set);
  writeJson(rulesKey(projectId), next);
  return next;
}

/** Stop watching. */
export function demote(projectId: string, ruleId: string): string[] {
  const cur = listPromoted(projectId);
  const next = cur.filter((id) => id !== ruleId);
  writeJson(rulesKey(projectId), next);
  return next;
}

/** User-authored rules for this project. */
export function listCustom(projectId: string): CustomRule[] {
  const raw = readJson<CustomRule[]>(customKey(projectId));
  return Array.isArray(raw) ? raw : [];
}

/** Insert-or-update a custom rule by id. Returns the new list. */
export function upsertCustom(projectId: string, rule: CustomRule): CustomRule[] {
  const cur = listCustom(projectId);
  const idx = cur.findIndex((r) => r.id === rule.id);
  const next = idx === -1 ? [...cur, rule] : cur.map((r, i) => (i === idx ? rule : r));
  writeJson(customKey(projectId), next);
  return next;
}

/** Remove a custom rule (also demotes it from the watchlist). */
export function deleteCustom(projectId: string, ruleId: string): { custom: CustomRule[]; promoted: string[] } {
  const custom = listCustom(projectId).filter((r) => r.id !== ruleId);
  writeJson(customKey(projectId), custom);
  const promoted = demote(projectId, ruleId);
  return { custom, promoted };
}

/** Active project id from localStorage; falls back to demo id. */
export function getActiveProjectId(): string {
  try {
    const v = localStorage.getItem(env.VITE_LS_ACTIVE_PROJECT)
      ?? localStorage.getItem(env.VITE_LS_ACTIVE_PROJECT_LEGACY);
    if (typeof v === "string" && v.length > 0) return v;
  } catch { /* ignore */ }
  return env.VITE_FOYR_DEMO_PROJECT_ID;
}
